import { z } from "zod";

import {
  DOMAINS,
  type Difficulty,
  type Domain
} from "../contracts";

export const DIFFICULTIES = [1, 2, 3, 4, 5, 6] as const;

export const domainSchema = z.enum(DOMAINS) satisfies z.ZodType<Domain>;

export const difficultySchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6)
]) satisfies z.ZodType<Difficulty>;

export const timestampSchema = z.iso.datetime({ offset: true });

export const entityIdSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(
    /^[A-Za-z0-9](?:[A-Za-z0-9._:-]{0,126}[A-Za-z0-9])?$/,
    "Expected an opaque identifier"
  );

export const kebabCaseIdentifierSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Expected a lowercase kebab-case identifier"
  );

const levelIdPattern = new RegExp(
  "^(?:" + DOMAINS.join("|") + ")-[a-z0-9]+(?:-[a-z0-9]+)*-[1-6]$"
);

export const levelIdSchema = z
  .string()
  .max(160)
  .regex(levelIdPattern, "Expected a domain-activity-level identifier");

export function boundedTextSchema(maxLength: number) {
  return z
    .string()
    .min(1)
    .max(maxLength)
    .refine((value) => value === value.trim(), {
      message: "Leading or trailing whitespace is not allowed"
    })
    .refine(
      (value) =>
        Array.from(value).every((character) => {
          const codePoint = character.codePointAt(0);
          return codePoint !== undefined && codePoint >= 32 && codePoint !== 127;
        }),
      {
        message: "Control characters are not allowed"
      }
    );
}

export function isLocalResourceReference(value: string): boolean {
  if (
    value.startsWith("//") ||
    value.includes("\\") ||
    value.includes("?") ||
    value.includes("#") ||
    /^[A-Za-z][A-Za-z0-9+.-]*:/.test(value)
  ) {
    return false;
  }

  return !value.split("/").some((segment) => segment === "..");
}

export const localResourceReferenceSchema = boundedTextSchema(256).refine(
  isLocalResourceReference,
  { message: "Expected a local resource reference without traversal" }
);

export const localOggAudioReferenceSchema =
  localResourceReferenceSchema.refine(
    (value) => value.startsWith("/audio/") && value.endsWith(".ogg"),
    { message: "Expected a local /audio/*.ogg reference" }
  );

export const nonNegativeSafeIntegerSchema = z
  .number()
  .int()
  .min(0)
  .max(Number.MAX_SAFE_INTEGER);

export function hasUniqueValues<T>(values: readonly T[]): boolean {
  return new Set(values).size === values.length;
}

export function isLevelIdFor(
  levelId: string,
  domain: Domain,
  difficulty: Difficulty
): boolean {
  const prefix = domain + "-";
  const suffix = "-" + difficulty;
  const activity = levelId.slice(prefix.length, -suffix.length);

  return (
    levelId.startsWith(prefix) &&
    levelId.endsWith(suffix) &&
    kebabCaseIdentifierSchema.safeParse(activity).success
  );
}
